"use client";
import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import Image from "next/image";
import Link from "next/link";
import { pxToRem } from "@/util";
import { AppColors } from "@/assets/colors";
import { useQuery } from "@tanstack/react-query";
import { getBlogsApi } from "@/util/api/blog";
import { IBlog } from "@/util/types/blog";

function slugify(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function PublishedBlog() {
  const { classes } = styles();

  const { data, isLoading } = useQuery({
    queryKey: ["publishedBlogs"],
    queryFn: () =>
      getBlogsApi({
        page: 1,
        limit: 6,
        status: "published", //  appropriate status
      }),
  });

  const blogs: IBlog[] = data?.payload || [];

  return (
    <Box className={classes.uploadPaper}>
      <Box className={classes.uploadSpace}>
        {isLoading
          ? Array.from({ length: 3 }).map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                width="100%"
                height={pxToRem(390)}
              />
            ))
          : blogs.map((blog) => (
              <Box key={blog._id} className={classes.blogSection}>
                <Box className={classes.blogImage}>
                  <Image
                    src={
                      typeof blog.coverImage === "string"
                        ? blog.coverImage
                        : blog.coverImage?.url || "/fallback.jpg"
                    }
                    alt={blog.title}
                    fill
                    style={{ objectFit: "cover", objectPosition: "center" }}
                    priority
                  />
                </Box>

                <Box className={classes.blogContent}>
                  <Typography className={classes.blogDate}>
                    {new Date(blog.createdAt).toLocaleDateString()}
                  </Typography>
                  <Typography className={classes.blogTitle}>
                    {blog.title}
                  </Typography>

                  <Box className={classes.bottomSection}>
                    <Box className={classes.view}>
                      <Image
                        src="/images/dashboard/blog/vieweye.svg"
                        alt="view icon"
                        width={20}
                        height={20}
                        priority
                      />
                      <Typography className={classes.blogView}>
                        {blog.views ?? "0"} Views
                      </Typography>
                    </Box>

                    <Box className={classes.moreBox}>
                      <Link
                        href={`/blog/${slugify(blog.title)}-${blog._id}`}
                        className={classes.alignment}
                      >
                        <Typography className={classes.readMore}>
                          Read More
                        </Typography>
                        <Box className={classes.nextIcon}>
                          <Image
                            src="/images/dashboard/blog/right.svg"
                            alt="Arrow right icon"
                            width={20}
                            height={20}
                            priority
                          />
                        </Box>
                      </Link>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
      </Box>
    </Box>
  );
}

const styles = makeStyles()((theme) => ({
  uploadPaper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  uploadSpace: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
    gap: theme.spacing(4),
    width: "100%",
  },
  nextIcon: {
    display: "flex",
    flexDirection: "row",
  },
  blogSection: {
    height: pxToRem(390),
    display: "flex",
    flexDirection: "column",
    borderRadius: pxToRem(8),
    overflow: "hidden",
    backgroundColor: "#fff",
    border: `${pxToRem(1)} solid ${AppColors.neutral[60]}`,
    boxSizing: "border-box",
    width: "100%",
  },

  blogImage: {
    position: "relative",
    width: "100%",
    height: pxToRem(200),
    flexShrink: 0,
  },

  blogContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    padding: pxToRem(16),
    backgroundColor: "#fff",
  },
  blogDate: {
    fontWeight: 700,
    fontSize: pxToRem(17),
    color: AppColors.primary[300],
    fontFamily: "General sans",
  },
  blogTitle: {
    fontWeight: 600,
    fontSize: pxToRem(24),
    color: AppColors.primary[600],
    lineHeight: "130%",
    width: "80%",
  },
  blogView: {
    fontWeight: 500,
    fontSize: pxToRem(14),
    color: AppColors.text.second,
    [theme.breakpoints.down("sm")]: {
      // fontWeight: 600,
    },
  },
  view: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down("sm")]: {},
  },
  moreBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyItems: "center",
  },
  readMore: {
    color: AppColors.primary["600"],
    fontSize: pxToRem(16),
    fontWeight: 600,
  },
  bottomSection: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    justifyContent: "space-between",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "start",
      gap: theme.spacing(2),
      alignItems: "end",
    },
  },
  alignment: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
}));
